import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

const AuthStateWrapper = ({ children }) => {
  const { user: amplifyUser } = useAuthenticator((context) => [context.user]);
  const [email, setEmail] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Single read-from-Amplify routine. Used both on mount, on Authenticator
  // state change, and on Hub auth events (e.g. federated sign-in completes
  // outside the <Authenticator> component).
  const syncSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      if (payload?.email) {
        setEmail(payload.email);
        setUser(payload);
      } else {
        setEmail(null);
        setUser(null);
      }
    } catch {
      setEmail(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    syncSession();
  }, [amplifyUser, syncSession]);

  useEffect(() => {
    // Catches sign-in events that the Authenticator hook doesn't see —
    // notably the Google / Hosted UI redirect callback — and clears state
    // when token refresh fails, so the user gets bounced to /login instead
    // of staring at a half-broken authenticated view.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          syncSession();
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          setEmail(null);
          setUser(null);
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, [syncSession]);

  const signOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setEmail(null);
      setUser(null);
      navigate('/');
    }
  };

  return (
    <AuthContext.Provider value={{ email, setEmail, user, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  return (
    <Authenticator.Provider>
      <AuthStateWrapper>{children}</AuthStateWrapper>
    </Authenticator.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
